import { previousElement } from '@uplift/api';
import { FormElement, FormSubmissionResponse } from '@uplift/types';
import { FormikState } from 'formik';
import React from 'react';
import type { FormType } from './types';
import { Action } from './render/render-form.context';

export const getPrevious = async (
  submissionId: string,
  element: FormElement,
  dispatch: React.Dispatch<Action>,
  push: (url: string) => void
) => {
  dispatch({
    type: 'getElement'
  });

  const result = await previousElement({
    submission_id: submissionId,
    form_element_id: element.formElementId
  });

  if ('error' in result) {
    console.error(result.error);
    return;
  }

  const answerVal = getAnswerValue(result.element, result.response);

  dispatch({
    type: 'setEditElement',
    payload: {
      element: result.element,
      answerVal,
    }
  });

  push(`/form/${submissionId}/page/${result.element.orderNumber}`);
};

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const valToString = (value: string | string[]) => {
  if (Array.isArray(value)) {
    return;
  }

  if (!value) {
    return '';
  } else {
    return value;
  }
};

const getMultiSelectIds = (value: string | string[]) => Array.isArray(value)
  ? value.filter(x => x).filter(x => uuidRegex.test(x)).join('|')
  : value;

const getMultiSelectOther = (value: string | string[]) => Array.isArray(value)
  ? value.filter(x => x).find(x => !uuidRegex.test(x))
  : value;

type NextElementRequestBase = {
  submission_id: string;
  form_element_id: string;
};

type NextElementRequest =
  | NextElementRequestBase & {
    form_element_answer_id: string;
  }
  | NextElementRequestBase & {
    response_text: string;
  }
  | NextElementRequestBase & {
    form_element_answer_id: string;
    response_text: string;
  };

export const getRequest = (submissionId: string, element: FormElement, formik: FormikState<FormType>, val?: string): NextElementRequest => {
  const common = {
    submission_id: submissionId,
    form_element_id: element.formElementId,
  };

  const value = val ?? formik.values[element.formElementId];

  switch (element.formElementDefinition.formElementType) {
    case 'single_select':
    case 'page':
      return {
        ...common,
        form_element_answer_id: valToString(value)
      };
    case 'multi_select':
      return {
        ...common,
        form_element_answer_id: getMultiSelectIds(value),
        response_text: getMultiSelectOther(value),
      };
    case 'short_answer':
    case 'simple_input':
    default:
      return {
        ...common,
        response_text: valToString(value)
      };
  }
};


export const getAnswerValue = (element: FormElement, response: FormSubmissionResponse): string | string[] => {
  let answerVal: string | string[] = response.responseText;

  if ([ 'single_select', 'page' ].includes(element.formElementDefinition.formElementType)) {
    answerVal = response.formElementAnswerId;
  } else if (element.formElementDefinition.formElementType === 'multi_select') {
    answerVal = response.formElementAnswerId?.split('|')?.concat(response.responseText) ?? [];
  }

  return answerVal;
};
